import {
  HeadlessMenu,
  type HeadlessMenuRootProps,
  type HeadlessMenuTriggerProps,
  type HeadlessMenuPopupProps,
  type HeadlessMenuItemProps,
  type HeadlessMenuGroupProps,
  type HeadlessMenuGroupLabelProps,
  type HeadlessMenuSeparatorProps,
} from '@design-system/headless-ui';
import { cn } from '@foundation/utils/cn';

export type MenuRootProps = HeadlessMenuRootProps;
export type MenuTriggerProps = HeadlessMenuTriggerProps & {
  readonly 'data-testid'?: string;
};
export type MenuPopupProps = Omit<
  HeadlessMenuPopupProps,
  'className' | 'style' | 'render'
> & { readonly 'data-testid'?: string };
export type MenuItemVariant = 'default' | 'danger';
export type MenuItemProps = Omit<
  HeadlessMenuItemProps,
  'className' | 'style' | 'render'
> & {
  readonly variant?: MenuItemVariant;
  readonly 'data-testid'?: string;
};
export type MenuGroupProps = Omit<
  HeadlessMenuGroupProps,
  'className' | 'style' | 'render'
> & { readonly 'data-testid'?: string };
export type MenuGroupLabelProps = Omit<
  HeadlessMenuGroupLabelProps,
  'className' | 'style' | 'render'
> & { readonly 'data-testid'?: string };
export type MenuSeparatorProps = Omit<
  HeadlessMenuSeparatorProps,
  'className' | 'style' | 'render'
> & { readonly 'data-testid'?: string };

const VARIANTS = {
  default: 'text-pmui-fg-primary',
  danger: 'text-pmui-fg-status-negative-default',
} as const satisfies Record<MenuItemVariant, string>;

function Popup(props: MenuPopupProps) {
  return (
    <HeadlessMenu.Popup
      {...props}
      className="w-[216px] max-w-[calc(100vw-2rem)] max-h-(--available-height) overflow-y-auto rounded-md border border-pmui-border-primary bg-pmui-bg-primary p-1 shadow-sm outline-hidden origin-(--transform-origin) transition-[opacity,transform] duration-150 data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0 motion-reduce:transition-none"
    />
  );
}

function Item({ variant = 'default', ...props }: MenuItemProps) {
  return (
    <HeadlessMenu.Item
      {...props}
      className={cn(
        'min-h-12 cursor-pointer gap-2 rounded-md px-3 py-3 text-base leading-6 tracking-tight outline-hidden data-highlighted:bg-pmui-interactive-bg-default-hovered data-disabled:cursor-not-allowed data-disabled:text-pmui-interactive-fg-disabled',
        VARIANTS[variant],
      )}
    />
  );
}

function Group(props: MenuGroupProps) {
  return <HeadlessMenu.Group {...props} />;
}

function GroupLabel(props: MenuGroupLabelProps) {
  return (
    <HeadlessMenu.GroupLabel
      {...props}
      className="px-2 py-2 text-xs font-emphasis text-pmui-fg-secondary"
    />
  );
}

function Separator(props: MenuSeparatorProps) {
  return (
    <HeadlessMenu.Separator
      {...props}
      className="mx-2 my-2 h-px bg-pmui-border-primary"
    />
  );
}

export const Menu = {
  Root: HeadlessMenu.Root,
  Trigger: HeadlessMenu.Trigger,
  Popup,
  Item,
  Group,
  GroupLabel,
  Separator,
};
